/*
 * Veterinary Repository — professional data room (client sub-block).
 * ACCESS-GATED: only buyers with an APPROVED registration for the auction
 * (plus the owning organizer / admins — enforced SERVER-SIDE) see documents.
 * Everyone else gets an elegant gate with a direct Register-to-Bid flow that
 * unlocks the room the moment approval lands.
 * Dynamic categories only: a category renders solely when it has documents.
 */
import {
  BadgeCheck,
  Bone,
  Download,
  FileText,
  FlaskConical,
  FolderDown,
  FolderOpen,
  ImageIcon,
  Loader2,
  Lock,
  RotateCw,
  ScanLine,
  Stethoscope,
  Waves,
} from "lucide-react";
import { useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { RegisterToBidDialog } from "@/components/bidding/RegisterToBidDialog";
import { MediaLightbox } from "@/components/media/MediaLightbox";
import { LearnMore } from "@/components/resources/LearnMore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { BACKEND_URL, api } from "@/lib/api";
import { localized } from "@/lib/intl";

// display order + icons — keep keys in sync with backend VET_CATEGORIES
const CATEGORY_META = [
  { key: "radiographs", icon: Bone },
  { key: "veterinary_reports", icon: FileText },
  { key: "endoscopy", icon: ScanLine },
  { key: "ultrasounds", icon: Waves },
  { key: "clinical_examination", icon: Stethoscope },
  { key: "blood_tests", icon: FlaskConical },
  { key: "certificates", icon: BadgeCheck },
  { key: "additional_documents", icon: FolderOpen },
];

const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fetchBlobUrl = async (docId) => {
  // proxy=1: el backend sirve los bytes en el mismo origen — sin CORS y sin
  // presigned URLs caducadas (Fase 12B, bug real: pantalla negra + loader infinito)
  const { data } = await api.get(`/media/file/${docId}`, {
    responseType: "blob",
    params: { proxy: 1 },
    timeout: 45000,
  });
  return { url: URL.createObjectURL(data), type: data.type };
};

/* ---------- locked gate ---------- */
const Gate = ({ repo, onUnlock }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [registerOpen, setRegisterOpen] = useState(false);
  const ev = repo.register_event;

  return (
    <div className="rounded-3xl border border-black/8 bg-[#FAFAF8] p-8 sm:p-10 flex flex-col items-center text-center gap-5" data-testid="vet-repo-gate">
      <span className="w-14 h-14 rounded-full bg-white border border-black/8 flex items-center justify-center" aria-hidden="true">
        <Lock className="w-5 h-5 text-[#1D4D3B]" />
      </span>
      <div className="flex flex-col gap-2 max-w-md">
        <p className="text-sm text-[#737373] leading-relaxed" data-testid="vet-repo-gate-copy">{t("vet.lockedBody")}</p>
        <p className="text-xs text-[#0A0A0A]/45">{t("vet.docCount", { count: repo.total })}</p>
        <LearnMore slug="registering-and-verifying-your-account" section="auction-registration" className="justify-center" />
      </div>
      <div className="flex flex-wrap justify-center gap-2" data-testid="vet-repo-gate-categories">
        {repo.categories.map((c) => (
          <span key={c} className="inline-flex items-center rounded-full border border-black/10 bg-white px-3 py-1 text-[11px] font-medium text-[#0A0A0A]/60">
            {t(`vet.${c}`)}
          </span>
        ))}
      </div>
      {ev && (
        <button
          type="button"
          data-testid="vet-repo-register"
          onClick={() => (user ? setRegisterOpen(true) : navigate("/signin"))}
          className="inline-flex items-center h-12 px-8 rounded-full bg-[#1D4D3B] text-white text-sm font-semibold hover:bg-[#163B2D] transition-colors duration-200"
        >
          {user ? t("vet.registerCta") : t("vet.signInCta")}
        </button>
      )}
      {ev && user && (
        <RegisterToBidDialog
          open={registerOpen}
          onOpenChange={setRegisterOpen}
          eventId={ev.id}
          eventVisibility={ev.visibility}
          onRegistered={(result) => {
            if (result?.status === "approved") onUnlock();
          }}
        />
      )}
      {ev && (
        <p className="text-xs text-[#0A0A0A]/40">{localized(ev.title, i18n.resolvedLanguage)}</p>
      )}
    </div>
  );
};

/* ---------- unlocked data room ---------- */
const DocRow = ({ doc, onPreview }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const isImage = (doc.content_type || "").startsWith("image/") || doc.kind === "photo";

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/media/file/${doc.id}`, {
        responseType: "blob",
        params: { proxy: 1, download: 1 },
        timeout: 120000,
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error(t("vet.downloadFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex items-center gap-3 py-3" data-testid={`vet-doc-${doc.id}`}>
      <span className="w-9 h-9 rounded-xl bg-black/[0.04] flex items-center justify-center shrink-0" aria-hidden="true">
        {isImage ? <ImageIcon className="w-4 h-4 text-[#0A0A0A]/50" /> : <FileText className="w-4 h-4 text-[#0A0A0A]/50" />}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0A0A0A] truncate">{doc.original_filename}</p>
        <p className="text-[11px] text-[#737373] uppercase tracking-wide">
          {isImage ? "IMAGE" : "PDF"}
          {doc.size ? ` · ${fmtSize(doc.size)}` : ""}
        </p>
      </div>
      <button
        type="button"
        data-testid={`vet-doc-preview-${doc.id}`}
        onClick={() => onPreview(doc)}
        className="shrink-0 inline-flex items-center h-9 px-4 rounded-full bg-black/5 text-xs font-semibold text-[#0A0A0A] hover:bg-black/10 transition-colors duration-200"
      >
        {t("vet.preview")}
      </button>
      <button
        type="button"
        data-testid={`vet-doc-download-${doc.id}`}
        onClick={download}
        disabled={busy}
        aria-label={t("vet.download")}
        className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/5 text-[#0A0A0A] hover:bg-black/10 transition-colors duration-200 disabled:opacity-40"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
      </button>
    </li>
  );
};

const PreviewDialog = ({ doc, onClose }) => {
  const { t } = useTranslation();
  const [blob, setBlob] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    let url;
    setBlob(null);
    setFailed(false);
    fetchBlobUrl(doc.id)
      .then((b) => {
        if (!alive) {
          URL.revokeObjectURL(b.url);
          return;
        }
        url = b.url;
        setBlob(b);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc.id, attempt]);

  const isImage = (blob?.type || doc.content_type || "").startsWith("image/");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl w-[calc(100vw-1.5rem)] sm:w-[94vw] rounded-3xl p-0 gap-0 overflow-hidden"
        data-testid="vet-preview-dialog"
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 pb-3">
          <DialogTitle className="text-base font-semibold text-[#0A0A0A] truncate pr-10 text-left">
            {doc.original_filename}
          </DialogTitle>
        </DialogHeader>
        <div className="relative bg-[#111318] min-h-[320px] sm:min-h-[420px] max-h-[72vh]">
          {failed ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center"
              data-testid="vet-preview-error"
            >
              <p className="text-sm text-white/70 leading-relaxed max-w-sm">{t("vet.previewFailed")}</p>
              <button
                type="button"
                data-testid="vet-preview-retry"
                onClick={() => setAttempt((a) => a + 1)}
                className="inline-flex items-center gap-2 h-10 px-6 rounded-full bg-white text-[#0A0A0A] text-xs font-semibold hover:bg-white/90 transition-colors duration-200"
              >
                <RotateCw className="w-3.5 h-3.5" aria-hidden="true" />
                {t("vet.retry")}
              </button>
            </div>
          ) : !blob ? (
            <div className="absolute inset-0 flex items-center justify-center" data-testid="vet-preview-loading">
              <Loader2 className="w-6 h-6 text-white/50 animate-spin" aria-hidden="true" />
            </div>
          ) : isImage ? (
            <div className="w-full h-full min-h-[320px] sm:min-h-[420px] max-h-[72vh] flex items-center justify-center">
              <img src={blob.url} alt={doc.original_filename} className="max-h-[72vh] w-auto max-w-full object-contain" data-testid="vet-preview-image" />
            </div>
          ) : (
            <iframe src={blob.url} title={doc.original_filename} className="w-full h-[68vh] sm:h-[72vh] bg-white" data-testid="vet-preview-pdf" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const VetRepository = ({ docs = [], repo, onUnlock, horseId }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { category, items }

  if (!repo || repo.total === 0) return null; // dynamic: no docs, no section

  const grouped = {};
  for (const d of docs) {
    const cat = CATEGORY_META.some((c) => c.key === d.vet_category) ? d.vet_category : "additional_documents";
    (grouped[cat] = grouped[cat] || []).push(d);
  }
  const cats = CATEGORY_META.filter((c) => grouped[c.key]?.length);
  const isImage = (d) => (d.content_type || "").startsWith("image/") || d.kind === "photo";
  const archiveHref = (category) => `${BACKEND_URL}/api/media/vet/archive/${horseId}/${category}`;

  return (
    <section className="flex flex-col gap-6" data-testid="vet-repository">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("vet.repository")}</h2>
        {!repo.locked && (
          <span className="text-xs text-[#737373]" data-testid="vet-repo-count">{t("vet.docCount", { count: repo.total })}</span>
        )}
      </div>

      {repo.locked ? (
        <Gate repo={repo} onUnlock={onUnlock} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="vet-repo-room">
          {cats.map(({ key, icon: Icon }) => {
            const images = grouped[key].filter(isImage);
            const files = grouped[key].filter((d) => !isImage(d));
            return (
              <div key={key} className="rounded-3xl border border-black/8 bg-white p-6 flex flex-col gap-1" data-testid={`vet-category-${key}`}>
                <div className="flex items-center gap-3 pb-2 border-b border-black/5">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0" style={{ backgroundColor: "#1D4D3B" }} aria-hidden="true">
                    <Icon className="w-4 h-4" />
                  </span>
                  <h3 className="text-sm font-semibold text-[#0A0A0A]">{t(`vet.${key}`)}</h3>
                  <span className="ml-auto text-[11px] text-[#737373] tabular-nums">{grouped[key].length}</span>
                </div>
                <ul className="divide-y divide-black/5">
                  {/* Fase 12C: N imágenes = UNA entrada que abre el carrusel a
                      pantalla completa — nada de listados infinitos por archivo. */}
                  {images.length > 0 && (
                    <li className="flex flex-wrap items-center gap-3 py-3" data-testid={`vet-images-entry-${key}`}>
                      <span className="w-9 h-9 rounded-xl bg-black/[0.04] flex items-center justify-center shrink-0" aria-hidden="true">
                        <ImageIcon className="w-4 h-4 text-[#0A0A0A]/50" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0A0A0A]">{t("vet.imagesSet", { count: images.length })}</p>
                        <p className="text-[11px] text-[#737373] uppercase tracking-wide">
                          {fmtSize(images.reduce((acc, d) => acc + (d.size || 0), 0))}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid={`vet-view-images-${key}`}
                          onClick={() => setLightbox({ category: key, items: images })}
                          className="shrink-0 inline-flex items-center h-9 px-4 rounded-full bg-[#1D4D3B] text-xs font-semibold text-white hover:bg-[#163B2D] transition-colors duration-200"
                        >
                          {t("vet.viewImages", { count: images.length })}
                        </button>
                        <a
                          href={archiveHref(key)}
                          data-testid={`vet-download-all-${key}`}
                          aria-label={t("vet.downloadAll")}
                          title={t("vet.downloadAll")}
                          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/5 text-[#0A0A0A] hover:bg-black/10 transition-colors duration-200"
                        >
                          <FolderDown className="w-4 h-4" aria-hidden="true" />
                        </a>
                      </div>
                    </li>
                  )}
                  {files.map((d) => (
                    <DocRow key={d.id} doc={d} onPreview={setPreview} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {preview && <PreviewDialog doc={preview} onClose={() => setPreview(null)} />}
      {lightbox && (
        <MediaLightbox
          items={lightbox.items}
          startIndex={0}
          title={t(`vet.${lightbox.category}`)}
          downloadAllHref={horseId ? archiveHref(lightbox.category) : undefined}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
};
